import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AvailabilityCalendar from '../components/admin/availability/AvailabilityCalendar';
import BulkUpdateModal from '../components/admin/availability/BulkUpdateModal';
import DayEditorDrawer from '../components/admin/availability/DayEditorDrawer';
import { HostSection } from '../components/host/HostLayout';
import { hostListProperties } from '../api/host';
import { apiGet, apiPost, apiPut } from '../services/apiClient';

const dateKey = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatMoney = (value, currency = 'INR') => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(num);
  } catch (_) {
    return `${currency} ${num.toLocaleString('en-IN')}`;
  }
};

const formatMonthLabel = (date) => date.toLocaleString('en-US', { month: 'long', year: 'numeric' });

const isWeekendDay = (dayIdx) => dayIdx === 5 || dayIdx === 6; // Friday + Saturday

const buildRangeDates = (range, scope, todayIso) => {
  if (!range?.from || !range?.to) return [];
  const start = new Date(range.from);
  const end = new Date(range.to);
  const from = start < end ? start : end;
  const to = start < end ? end : start;
  const dates = [];
  for (let d = new Date(from.getTime()); d <= to; d = new Date(d.getTime() + 24 * 60 * 60 * 1000)) {
    const key = dateKey(d);
    if (key < todayIso) continue;
    const dow = d.getDay();
    if (scope === 'weekends' && !isWeekendDay(dow)) continue;
    if (scope === 'weekdays' && isWeekendDay(dow)) continue;
    dates.push(key);
  }
  return dates;
};

const HostCalendar = ({ accessToken }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthsToShow = 1;
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [days, setDays] = useState([]);
  const [basePricing, setBasePricing] = useState(null);
  const [calendarCounts, setCalendarCounts] = useState({ open: 0, blocked: 0, booked: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [action, setAction] = useState('block');
  const [priceScope, setPriceScope] = useState('all');
  const [priceInput, setPriceInput] = useState('');
  const [minNightsInput, setMinNightsInput] = useState('');
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [drawer, setDrawer] = useState({ open: false, date: null, day: null });
  const [bulkState, setBulkState] = useState({ open: false, range: null, defaults: {} });
  const [pricingForm, setPricingForm] = useState({
    weekday: '',
    weekend: '',
    weeklyDiscount: '',
    monthlyDiscount: '',
    cleaningFee: '',
    extraGuest: '',
    defaultMinNights: '',
  });
  const [pricingSaving, setPricingSaving] = useState(false);
  const todayIso = useMemo(() => dateKey(new Date()), []);
  const dayMap = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);
  const selectionRange = selectedRange || (selectedDay ? { from: selectedDay.date, to: selectedDay.date } : null);
  const selectionLabel = selectionRange ? `${selectionRange.from} -> ${selectionRange.to}` : 'None';
  const propertyCurrency = basePricing?.currency || selectedProperty?.currency || 'INR';

  const availabilitySummary = useMemo(() => {
    if (calendarCounts.open || calendarCounts.blocked || calendarCounts.booked) return calendarCounts;
    const totals = { open: 0, blocked: 0, booked: 0 };
    days.forEach((d) => {
      const status = (d.status || '').toLowerCase();
      if (status === 'booked') totals.booked += 1;
      else if (status === 'blocked' || d.is_available === false) totals.blocked += 1;
      else totals.open += 1;
    });
    return totals;
  }, [days, calendarCounts]);

  const loadProperties = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await hostListProperties(accessToken, { limit: 50 });
      const list = res?.items || [];
      setProperties(list);
      if (!selectedProperty && list.length) {
        const queryId = searchParams.get('propertyId');
        const match = queryId ? list.find((p) => String(p.id) === String(queryId)) : null;
        setSelectedProperty(match || list[0]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadBasePricing = async (prop = selectedProperty) => {
    if (!prop) return;
    try {
      const res = await apiGet(`/api/host/pricing/base?propertyId=${prop.id}`, accessToken);
      const pricing = res?.basePricing || res || null;
      if (pricing) {
        setBasePricing(pricing);
        setPricingForm({
          weekday: pricing.weekday_price ?? '',
          weekend: pricing.weekend_price ?? '',
          weeklyDiscount: pricing.weekly_discount_pct ?? '',
          monthlyDiscount: pricing.monthly_discount_pct ?? '',
          cleaningFee: pricing.cleaning_fee ?? '',
          extraGuest: pricing.extra_guest_fee ?? '',
          defaultMinNights: pricing.default_min_nights ?? '',
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load base pricing');
    }
  };

  const loadCalendar = async (prop = selectedProperty, anchor = month) => {
    if (!prop) return;
    setLoading(true);
    setError('');
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + monthsToShow, 0);
    const from = start.toISOString().slice(0, 10);
    const to = end.toISOString().slice(0, 10);
    try {
      const res = await apiGet(
        `/api/host/calendar?propertyId=${prop.id}&start=${from}&end=${to}`,
        accessToken
      );
      const list = res?.days || [];
      setDays(list);
      setCalendarCounts({
        open: res?.open_count || 0,
        blocked: res?.blocked_count || 0,
        booked: res?.booked_count || 0,
      });
      if (selectedDay) {
        const match = list.find((d) => d.date === selectedDay.date);
        setSelectedDay((prev) => (prev ? { ...prev, day: match || prev.day } : prev));
        setDrawer((prev) => (prev.open ? { ...prev, day: match || prev.day } : prev));
      }
    } catch (err) {
      setError(err.message || 'Failed to load availability');
      setDays([]);
      setCalendarCounts({ open: 0, blocked: 0, booked: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (selectedProperty) {
      setBasePricing(null);
      loadBasePricing(selectedProperty);
      loadCalendar(selectedProperty, month);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, month]);

  const handleMonthChange = (delta) => {
    const next = new Date(month.getFullYear(), month.getMonth() + delta, 1);
    const min = new Date();
    min.setDate(1);
    setMonth(next < min ? min : next);
  };

  const clearSelection = () => {
    setSelectedRange(null);
    setSelectedDay(null);
    setDrawer({ open: false, date: null, day: null });
    setAction('block');
    setError('');
  };
  const applySelection = async () => {
    if (!selectedProperty) return;
    if (!selectionRange) {
      setError('Select at least one date on the calendar.');
      return;
    }
    const targetDates = buildRangeDates(selectionRange, priceScope, todayIso);
    if (!targetDates.length) {
      setError('Choose future dates to update.');
      return;
    }
    const locked = targetDates.filter((d) => (dayMap.get(d)?.status || '').toLowerCase() === 'booked');
    const dates = targetDates.filter((d) => (dayMap.get(d)?.status || '').toLowerCase() !== 'booked');
    if (!dates.length) {
      setError('Booked dates cannot be changed.');
      return;
    }

    const actionMap = {
      block: 'block',
      unblock: 'unblock',
      price: 'set_price',
      min_nights: 'set_min_nights',
      clear: 'clear_overrides',
    };
    const payload = {
      propertyId: selectedProperty.id,
      startDate: dates[0],
      endDate: dates[dates.length - 1],
      applyOn: priceScope === 'weekends' ? 'weekend_only' : priceScope === 'weekdays' ? 'weekdays_only' : 'all_days',
      action: actionMap[action],
    };
    if (action === 'price') {
      const price = Number(priceInput);
      if (!Number.isFinite(price) || price <= 0) {
        setError('Enter a valid nightly price.');
        return;
      }
      payload.priceOverride = price;
    }
    if (action === 'min_nights') {
      const min = Number(minNightsInput);
      if (!Number.isFinite(min) || min < 1) {
        setError('Enter minimum nights (1 or more).');
        return;
      }
      payload.minNightsOverride = min;
    }
    if (!payload.action) {
      setError('Pick an action before applying.');
      return;
    }

    setLoading(true);
    setError(locked.length ? `Skipping ${locked.length} booked day(s).` : '');
    try {
      const res = await apiPost(`/api/host/calendar/bulk`, payload, accessToken);
      if (res?.skippedBookedDates?.length) {
        setError(`Skipped ${res.skippedBookedDates.length} booked day(s).`);
      }
      await loadCalendar();
      clearSelection();
      setPriceInput('');
      setMinNightsInput('');
    } catch (err) {
      setError(err.message || 'Failed to update selection');
    } finally {
      setLoading(false);
    }
  };
  const handleDayClick = (isoKey, day) => {
    if (!selectedProperty || isoKey < todayIso) return;
    const current = day || dayMap.get(isoKey) || {};
    setSelectedRange({ from: isoKey, to: isoKey });
    setSelectedDay({ date: isoKey, day: current });
    setPriceInput(current.price_override ?? current.price ?? '');
    setMinNightsInput(current.min_nights_override ?? current.min_nights ?? current.minNights ?? '');
    setDrawer({ open: true, date: isoKey, day: current });
    setAction('price');
  };
  const handleDrawerSave = async (payload) => {
    if (!selectedProperty || !drawer.date) return;
    const normalize = { ...payload };
    if (Object.prototype.hasOwnProperty.call(normalize, 'price_override')) {
      const val = normalize.price_override;
      normalize.price_override = val === '' || val === undefined ? null : Number(val);
    }
    if (Object.prototype.hasOwnProperty.call(normalize, 'min_nights_override')) {
      const val = normalize.min_nights_override;
      normalize.min_nights_override = val === '' || val === undefined ? null : Number(val);
    }
    const action =
      normalize.status === 'blocked'
        ? 'block'
        : normalize.status === 'available'
        ? normalize.price_override === null && normalize.min_nights_override === null
          ? 'clear_overrides'
          : 'unblock'
        : normalize.price_override !== null
        ? 'set_price'
        : normalize.min_nights_override !== null
        ? 'set_min_nights'
        : 'unblock';
    setLoading(true);
    setError('');
    try {
      await apiPost(
        `/api/host/calendar/bulk`,
        {
          propertyId: selectedProperty.id,
          startDate: drawer.date,
          endDate: drawer.date,
          action,
          priceOverride: normalize.price_override === null ? null : normalize.price_override,
          minNightsOverride: normalize.min_nights_override === null ? null : normalize.min_nights_override,
        },
        accessToken
      );
      await loadCalendar();
      setDrawer((prev) => ({ ...prev, open: false }));
    } catch (err) {
      setError(err.message || 'Failed to update day');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  const handleBulkSubmit = async (payload) => {
    if (!selectedProperty) return;
    const start = dateKey(payload.from);
    const end = dateKey(payload.to || payload.from);
    const weekdays =
      Array.isArray(payload.weekdays) && payload.weekdays.length
        ? payload.weekdays.map((w) => Number(w)).filter((w) => w >= 0 && w <= 6)
        : null;
    const dates = buildRangeDates({ from: start, to: end }, weekdays ? 'all' : 'all', todayIso).filter((d) => {
      const dow = new Date(d).getDay();
      return !weekdays || weekdays.includes(dow);
    });
    if (!dates.length) throw new Error('Select at least one future date.');

    const priceVal =
      payload.price_override === '' || payload.price_override === undefined ? undefined : Number(payload.price_override);
    const minVal =
      payload.min_nights_override === '' || payload.min_nights_override === undefined
        ? undefined
        : Number(payload.min_nights_override);
    const action =
      payload.action ||
      (payload.status === 'blocked'
        ? 'block'
        : payload.status === 'available'
        ? 'unblock'
        : priceVal !== undefined
        ? 'set_price'
        : minVal !== undefined
        ? 'set_min_nights'
        : 'unblock');
    const body = {
      propertyId: selectedProperty.id,
      startDate: dates[0],
      endDate: dates[dates.length - 1],
      action,
      applyOn: weekdays ? (weekdays.includes(5) && weekdays.includes(6) && weekdays.length === 2 ? 'weekend_only' : weekdays.length === 5 ? 'weekdays_only' : 'all_days') : 'all_days',
      ...(priceVal !== undefined && Number.isFinite(priceVal) ? { priceOverride: priceVal } : {}),
      ...(minVal !== undefined && Number.isFinite(minVal) ? { minNightsOverride: minVal } : {}),
      ...(payload.note ? { note: payload.note } : {}),
    };

    setLoading(true);
    setError('');
    try {
      await apiPost(`/api/host/calendar/bulk`, body, accessToken);
      await loadCalendar();
      setBulkState({ open: false, range: null, defaults: {} });
    } catch (err) {
      setError(err.message || 'Failed to update availability');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  const saveBasePricing = async () => {
    if (!selectedProperty) return;
    const toNumberOrNull = (val) => {
      if (val === '' || val === null || val === undefined) return null;
      const parsed = Number(val);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : NaN;
    };

    const weekdayPrice = toNumberOrNull(pricingForm.weekday);
    const weekendPrice = toNumberOrNull(pricingForm.weekend);
    const weeklyDiscount = toNumberOrNull(pricingForm.weeklyDiscount);
    const monthlyDiscount = toNumberOrNull(pricingForm.monthlyDiscount);
    const cleaningFee = toNumberOrNull(pricingForm.cleaningFee);
    const extraGuest = toNumberOrNull(pricingForm.extraGuest);
    const defaultMinNights = toNumberOrNull(pricingForm.defaultMinNights);

    const invalid =
      [weekdayPrice, weekendPrice, weeklyDiscount, monthlyDiscount, cleaningFee, extraGuest, defaultMinNights].some(
        (v) => Number.isNaN(v)
      );
    if (invalid) {
      setError('Enter valid numbers for base pricing.');
      return;
    }

    const payload = {
      propertyId: selectedProperty.id,
    };
    if (weekdayPrice !== null) payload.weekdayPrice = weekdayPrice;
    if (weekendPrice !== null) payload.weekendPrice = weekendPrice;
    if (weeklyDiscount !== null) payload.weeklyDiscountPct = weeklyDiscount;
    if (monthlyDiscount !== null) payload.monthlyDiscountPct = monthlyDiscount;
    if (cleaningFee !== null) payload.cleaningFee = cleaningFee;
    if (extraGuest !== null) payload.extraGuestFee = extraGuest;
    if (defaultMinNights !== null) payload.defaultMinNights = defaultMinNights;
    if (!Object.keys(payload).length) {
      setError('Add a price or fee before saving.');
      return;
    }

    setPricingSaving(true);
    setError('');
    try {
      const res = await apiPut('/api/host/pricing/base', payload, accessToken);
      const saved = res?.basePricing || res;
      if (saved) {
        setBasePricing(saved);
      }
      await loadCalendar();
    } catch (err) {
      setError(err.message || 'Failed to save base pricing');
    } finally {
      setPricingSaving(false);
    }
  };

  const selectionCount = selectionRange ? buildRangeDates(selectionRange, priceScope, todayIso).length : 0;

  return (
    <HostSection
      title="Calendar & Pricing"
      description="Control availability, nightly rates, and minimum stays across your listings."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
            <p className="text-base font-semibold text-slate-900">Select a property to manage availability</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedProperty?.id || ''}
              onChange={(e) => {
                const match = properties.find((p) => String(p.id) === e.target.value);
                setSelectedProperty(match || null);
                if (match) {
                  searchParams.set('propertyId', match.id);
                  setSearchParams(searchParams, { replace: true });
                }
              }}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">{loading ? 'Loading...' : 'Choose property'}</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || `Property ${p.id}`}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                loadBasePricing(selectedProperty);
                loadCalendar();
              }}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              disabled={!selectedProperty}
            >
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{error}</div>}

        {!selectedProperty && !loading && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-600">
            Select a property to view its availability calendar.
          </div>
        )}

        {selectedProperty && (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-3">
              <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800">Quick actions</p>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
                    {selectionLabel}
                  </span>
                </div>
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">Base prices</p>
                      <p>
                        Weekday:{' '}
                        {formatMoney(
                          basePricing?.weekday_price ?? selectedProperty.base_weekday_price ?? selectedProperty.base_price_per_night ?? 0,
                          propertyCurrency
                        )}
                      </p>
                      <p>
                        Weekend:{' '}
                        {formatMoney(
                          basePricing?.weekend_price ?? selectedProperty.base_weekend_price ?? selectedProperty.weekend_price_per_night ?? 0,
                          propertyCurrency
                        )}
                      </p>
                      <p>
                        Weekly:{' '}
                        {basePricing?.weekly_discount_pct
                          ? `${basePricing.weekly_discount_pct}% off`
                          : selectedProperty.weekly_discount
                          ? `${selectedProperty.weekly_discount}% off`
                          : '0% off'}
                      </p>
                      <p>
                        Monthly:{' '}
                        {basePricing?.monthly_discount_pct
                          ? `${basePricing.monthly_discount_pct}% off`
                          : selectedProperty.monthly_discount
                          ? `${selectedProperty.monthly_discount}% off`
                          : '0% off'}
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">Min/Max nights</p>
                      <p>
                        {basePricing?.default_min_nights || selectedProperty.min_nights || '-'} / {selectedProperty.max_nights || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                  {[{ key: 'block', label: 'Block dates' }, { key: 'unblock', label: 'Open dates' }, { key: 'price', label: 'Set price' }, { key: 'min_nights', label: 'Set min nights' }, { key: 'clear', label: 'Clear overrides' }].map((btn) => (
                    <button
                      key={btn.key}
                      type="button"
                      onClick={() => setAction(btn.key)}
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                        action === btn.key ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-700">Price override</p>
                    <span className="text-[11px] text-slate-500">Weekend = Fri & Sat</span>
                  </div>
                  <input
                    type="number"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    className="w-full rounded border border-slate-200 px-2 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Enter nightly price"
                  />
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {[{ key: 'all', label: 'All days' }, { key: 'weekends', label: 'Weekend only' }, { key: 'weekdays', label: 'Weekdays only' }].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`rounded-md border px-2 py-1 font-semibold ${
                          priceScope === opt.key
                            ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        onClick={() => setPriceScope(opt.key)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                  <p className="font-semibold text-slate-700">Minimum nights</p>
                  <input
                    type="number"
                    value={minNightsInput}
                    onChange={(e) => setMinNightsInput(e.target.value)}
                    className="w-full rounded border border-slate-200 px-2 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Minimum nights"
                  />
                </div>

                <button
                  type="button"
                  onClick={applySelection}
                  className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  disabled={loading || (!selectionRange && !selectedDay)}
                >
                  {loading ? 'Saving...' : `Apply to selection${selectionCount ? ` (${selectionCount})` : ''}`}
                </button>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-indigo-50 px-3 py-1 font-semibold text-indigo-700">
                    Selection: {selectionLabel}
                  </span>
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={clearSelection}
                  >
                    Clear selection
                  </button>
                </div>
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800">Base pricing</p>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
                    {propertyCurrency}
                  </span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Weekday price</span>
                    <input
                      type="number"
                      value={pricingForm.weekday}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, weekday: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Weekend price</span>
                    <input
                      type="number"
                      value={pricingForm.weekend}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, weekend: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Weekly discount %</span>
                    <input
                      type="number"
                      value={pricingForm.weeklyDiscount}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, weeklyDiscount: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Monthly discount %</span>
                    <input
                      type="number"
                      value={pricingForm.monthlyDiscount}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, monthlyDiscount: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Default min nights</span>
                    <input
                      type="number"
                      min="1"
                      value={pricingForm.defaultMinNights}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, defaultMinNights: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="1"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Cleaning fee</span>
                    <input
                      type="number"
                      value={pricingForm.cleaningFee}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, cleaningFee: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-semibold text-slate-700">
                    <span>Extra guest fee</span>
                    <input
                      type="number"
                      value={pricingForm.extraGuest}
                      onChange={(e) => setPricingForm((prev) => ({ ...prev, extraGuest: e.target.value }))}
                      className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                  onClick={saveBasePricing}
                  disabled={pricingSaving}
                >
                  {pricingSaving ? 'Saving prices...' : 'Save base pricing'}
                </button>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-3">
              <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{selectedProperty.title}</p>
                    <p className="text-xs text-slate-500">
                      {formatMonthLabel(month)} - {propertyCurrency} pricing
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      {selectedProperty.status || 'draft'}
                    </span>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Open {availabilitySummary.open}
                    </span>
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                      Booked {availabilitySummary.booked}
                    </span>
                    <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
                      Blocked {availabilitySummary.blocked}
                    </span>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() =>
                        setBulkState({
                          open: true,
      range: selectionRange || { from: dateKey(new Date()), to: dateKey(new Date()) },
      defaults: { status: action },
    })
  }
                    >
                      Bulk range update
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={clearSelection}
                    >
                      Clear selection
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      disabled
                      title="ICS/iCal export will be available soon"
                    >
                      Export iCal (soon)
                    </button>
                  </div>
                </div>

                <AvailabilityCalendar
                  month={month}
                  monthsToShow={monthsToShow}
                  days={days}
                  loading={loading}
                  onMonthChange={handleMonthChange}
                  onDayClick={handleDayClick}
                  onRangeSelect={(range) => {
                    setSelectedRange(range);
                    setSelectedDay(null);
                    setDrawer({ open: false, date: null, day: null });
                  }}
                  onQuickAction={(a) => setAction(a)}
                  selectedRange={selectionRange}
                  currency={propertyCurrency}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <BulkUpdateModal
        open={bulkState.open}
        range={bulkState.range}
        defaults={bulkState.defaults}
        onClose={() => setBulkState({ open: false, range: null, defaults: {} })}
        onSubmit={handleBulkSubmit}
      />
      <DayEditorDrawer
        open={drawer.open}
        date={drawer.date}
        day={drawer.day}
        onClose={() => setDrawer({ open: false, date: null, day: null })}
        onSave={handleDrawerSave}
        onClear={() =>
          handleDrawerSave({
            status: 'available',
            price_override: null,
            min_nights_override: null,
            note: '',
          })
        }
      />
    </HostSection>
  );
};

export default HostCalendar;
